import { Button, Card, Col, DatePicker, Form, Row, Select, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { API_METHODS, DATE_WISE_PRINTING_OPTIONS, DEFAULT_DATE_FORMAT } from '../../constants';
import { displayFormattedDate, formatCurrency, formatDateToDayJS, getClientConfig, isEmpty } from '../../generalFunctions';
import useApi from '../../hooks/useApi';
import PrintPdfModal from './PrintPdfModal';

const DateRangeWisePrinting = (props) => {
    const [form] = Form.useForm();
    const [response, request] = useApi("/search/dateRange", null, API_METHODS.POST);
    const [tableData, setTableData] = useState();
    const [visiblePrint, setVisiblePrint] = useState(false);
    const [selectedType, setSelectedType] = useState();
    const { account } = getClientConfig();

    const columns = [
        {
            title: "Account",
            dataIndex: "accountName",
            render: (text, record) => getAccountNameData(record)
        },
        {
            title: "Date",
            dataIndex: "date",
            render: (text, record) => displayFormattedDate(getDateData(record))
        },
        {
            title: "Total Amount",
            dataIndex: "totalAmount",
            render: (text, record) => getAmountData(record)
        }
    ];

    useEffect(() => {
        if (response) {
            const tableData = !isEmpty(response.data) ? response.data : [];
            setTableData(tableData);
        }
    }, [response]);

    const onSubmit = () => {
        form.validateFields().then(values => {
            setSelectedType(values.searchType);
            values.startDate = formatDateToDayJS(values.startDate);
            values.endDate = formatDateToDayJS(values.endDate);
            request(values);
        });
    }

    const onClear = () => {
        form.resetFields();
    }

    const handlePrint = (record) => {
        const flag = visiblePrint ? false : true;
        setVisiblePrint(flag);
    };

    const getAccountNameData = (record) => {
        switch (selectedType) {
            case "creditNote":
                return record.accountName;
            case "debitNote":
                return record.accountName;
            case "releaseOrder":
            case "classifiedReleaseOrder":
                return record.billFor;
            case "partyBill":
            case "classifiedPartyBill":
                return record.billFor;
            case "receipt":
                return record.receiptFor;
            case "paymentVoucher":
                return record.creditor;
            case "onAccount":
                return record.accountName;
        }
    }

    const getDateData = (record) => {
        switch (selectedType) {
            case "creditNote":
                return record.date;
            case "debitNote":
                return record.date;
            case "releaseOrder":
            case "classifiedReleaseOrder":
                return record.roDate;
            case "partyBill":
            case "classifiedPartyBill":
                return record.invoiceDate;
            case "receipt":
                return record.receiptDate;
            case "paymentVoucher":
                return record.voucherDate;
            case "onAccount":
                return record.paymentDate;
        }
    }

    const getAmountData = (record) => {
        switch (selectedType) {
            case "creditNote":
                return record.totalAmount;
            case "debitNote":
                return record.totalAmount;
            case "releaseOrder":
            case "classifiedReleaseOrder":
                return record.roAmount;
            case "partyBill":
            case "classifiedPartyBill":
                return record.finalBillAmount;
            case "receipt":
            case "onAccount":
                return formatCurrency(record.amount);
            case "paymentVoucher":
                return formatCurrency(record.finalAmount);
        }
    }

    return (
        <Space direction='vertical' size={24} style={{ width: "100%" }}>
            <Typography.Title level={5}>Date Range Wise Printing</Typography.Title>
            <Card>
                <Form
                    layout="vertical"
                    form={form}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="searchType"
                                label="Type"
                                rules={[{ required: true, message: "Type is required." }]}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                                        0
                                    }
                                >
                                    {DATE_WISE_PRINTING_OPTIONS.map((option) => (
                                        <Select.Option key={option.key} value={option.key}>
                                            {option.value}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="startDate"
                                label="Start Date"
                                rules={[{ required: true, message: "Start Date is required." }]}
                            >
                                <DatePicker format={DEFAULT_DATE_FORMAT} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="endDate"
                                label="End Date"
                                rules={[{ required: true, message: "End Date is required." }]}
                            >
                                <DatePicker format={DEFAULT_DATE_FORMAT} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row style={{ float: "right" }}>
                        <Col span={24}>
                            <Space>
                                {tableData && <Button type='primary' onClick={handlePrint} disabled={tableData?.length === 0}>Print All</Button>}
                                <Button type='primary' onClick={onSubmit}>Search</Button>
                                <Button type='default' onClick={onClear}>Clear</Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>
            {tableData &&
                <Table rowKey="record" pagination={false} columns={columns} dataSource={tableData} scroll={{ y: window.innerHeight * 0.4 }} />
            }

            {visiblePrint && (
                <PrintPdfModal
                    visible={visiblePrint}
                    data={tableData}
                    title={selectedType}
                    close={handlePrint}
                    pdfType={selectedType}
                    isClassified={selectedType.includes("classified") ? true : false}
                />
            )}
        </Space>
    );
}

export default DateRangeWisePrinting;